import { useCallback, useEffect, useState } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useGetFavoriteBrands from "@/shared/hooks/favorite-brands/useGetFavoriteBrands";
import useUpdateFavoriteBrands from "@/shared/hooks/favorite-brands/useUpdateFavoriteBrands";
import { MAX_FAVORITE_BRANDS } from "@/features/pathpick/features/search-products/constants";

const isSameIds = (a: number[], b: number[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

// 브랜드 카드의 즐겨찾기 별 토글.
// 서버에 add/remove가 없고 전체 목록 교체(PUT)만 있어, 현재 목록에서 하나를 더하거나 뺀 배열을 보낸다.
const useToggleFavoriteBrand = () => {
  const { t } = useI18nHelper();
  const { info, warning } = useAppToasts({ id: "GLOBAL" });
  const { favoriteBrands } = useGetFavoriteBrands();
  const { mutate: updateFavoriteBrands } = useUpdateFavoriteBrands();

  // 전송 중 낙관값 — 서버 재조회를 기다리면 별이 한 박자 늦게 바뀐다.
  const [pendingIds, setPendingIds] = useState<number[] | null>(null);

  const savedIds = favoriteBrands.brands.map((brand) => brand.id);
  const favoriteIds = pendingIds ?? savedIds;

  // 재조회 결과가 낙관값을 따라잡은 순간에 해제한다.
  // mutate의 onSettled에서 지우면 invalidate 재조회 전이라 옛 값으로 잠깐 되돌아간다.
  useEffect(() => {
    if (pendingIds && isSameIds(pendingIds, savedIds)) {
      setPendingIds(null);
    }
  }, [pendingIds, savedIds]);

  const isFavorite = useCallback(
    (brandId: number) => favoriteIds.includes(brandId),
    [favoriteIds]
  );

  // 브랜드명은 인기/즐겨찾기 두 응답에서 형태가 달라 훅에서 역조회하지 않고 카드가 넘겨준다.
  const toggle = useCallback(
    (brandId: number, brandName: string) => {
      const nextIds = favoriteIds.includes(brandId)
        ? favoriteIds.filter((id) => id !== brandId)
        : [...favoriteIds, brandId];

      if (nextIds.length > MAX_FAVORITE_BRANDS) {
        // error()는 에러 객체를 파싱하는 API라 번역된 문자열을 넘기면 "알 수 없는 오류"로 대체된다.
        warning(
          t("path_pick:search_products.popular.favorite_brands_limit", {
            count: MAX_FAVORITE_BRANDS,
          })
        );
        return;
      }

      const isAdding = nextIds.includes(brandId);

      // 별은 낙관적으로 먼저 바뀌지만, 토스트는 서버 반영이 끝난 뒤에 띄운다(실패 토스트는 수정 훅이 처리).
      setPendingIds(nextIds);
      updateFavoriteBrands(
        { brandIds: nextIds },
        {
          onSuccess: () =>
            info(
              t(
                isAdding
                  ? "path_pick:search_products.popular.favorite_brand_add_success"
                  : "path_pick:search_products.popular.favorite_brand_remove_success",
                { brandName }
              )
            ),
          onError: () => setPendingIds(null),
        }
      );
    },
    [favoriteIds, updateFavoriteBrands, info, warning, t]
  );

  return { isFavorite, toggle };
};

export default useToggleFavoriteBrand;
